import { Component, Element, h, Host, Prop, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'

type DividerOrientation = 'horizontal' | 'vertical'
type DividerThickness = 'small' | 'medium' | 'large'
type DividerBorderStyle = 'dashed' | 'solid'
type DividerAlign = 'start' | 'center' | 'end'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/divider?tab=designer).
 *
 * @slot default - Slot opcional para conteúdo dentro do divisor (texto, ícones ou elementos personalizados).
 */
@Component({
  tag: 'br-divider',
  styleUrl: 'divider.scss',
  shadow: true,
})
export class Divider {
  @Element() el: HTMLBrDividerElement

  /**
   * ID personalizado para o elemento, útil para acessibilidade e identificação única.
   * Se não for fornecido, um ID único será gerado automaticamente.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Orientação do divisor.Em modo vertical, posicione dentro de um container com `display: flex`.
   */
  @Prop({ reflect: true }) orientation: DividerOrientation = 'horizontal'

  /**
   * Espessura visual do divisor.
   */
  @Prop({ reflect: true }) thickness: DividerThickness = 'small'

  /**
   * Estilo de borda do divisor.
   */
  @Prop({ reflect: true }) borderStyle: DividerBorderStyle = 'solid'

  /**
   * Ativa variante de alto contraste para fundos escuros.
   */
  @Prop({ reflect: true }) isDarkMode: boolean = false

  /**
   * Cor do divisor.
   * Aceita os seguintes formatos de cor:
   * - Cores nomeadas do CSS: 'red', 'blue', 'green', 'yellow', etc.
   * - Códigos hexadecimais: '#ff0000', '#00ff00', '#0000ff', etc.
   * - Valores RGB: 'rgb(255, 0, 0)', 'rgb(0, 255, 0)', etc.
   * - Valores RGBA: 'rgba(255, 0, 0, 0.5)', 'rgba(0, 255, 0, 0.8)', etc.
   * - Valores HSL: 'hsl(0, 100%, 50%)', 'hsl(120, 100%, 50%)', etc.
   * - Valores HSLA: 'hsla(0, 100%, 50%, 0.5)', 'hsla(240, 100%, 50%, 0.7)', etc.
   * Se não definida, usa a cor padrão do tema.
   */
  @Prop({ reflect: true }) color: string = ''

  /**
   * Alinhamento do conteúdo quando há slot interno.
   */
  @Prop() align: DividerAlign

  /**
   * Faz o divisor ocupar toda a largura do container, compensando paddings laterais.
   */
  @Prop() bleed: boolean = false

  /**
   * Margin top em pixels; se omitido, aplica padrão interno.
   */
  @Prop() marginTop: number = null

  /**
   * Margin bottom em pixels; se omitido, aplica padrão interno.
   */
  @Prop() marginBottom: number = null

  /**
   * Margin left em pixels; se omitido, aplica padrão interno.
   */
  @Prop() marginLeft: number = null

  /**
   * Margin right em pixels; se omitido, aplica padrão interno.
   */
  @Prop() marginRight: number = null

  /**
   * Watcher para a cor do divisor.
   * Atualiza a propriedade CSS '--border-color' do elemento para refletir a cor definida.
   * Isso permite que o divisor use cores personalizadas sem precisar de estilos adicionais.
   */
  @Watch('color')
  updateColor() {
    this.el.style.setProperty('--border-color', this.color)
  }

  /**
   * Watcher para as propriedades de margin.
   * Atualiza as propriedades CSS customizáveis do elemento para refletir os margins definidos.
   */
  @Watch('marginTop')
  @Watch('marginBottom')
  @Watch('marginLeft')
  @Watch('marginRight')
  updateMargins() {
    if (this.marginTop !== null) {
      this.el.style.setProperty('--divider-margin-top', `${this.marginTop}px`)
    }
    if (this.marginBottom !== null) {
      this.el.style.setProperty('--divider-margin-bottom', `${this.marginBottom}px`)
    }
    if (this.marginLeft !== null) {
      this.el.style.setProperty('--divider-margin-left', `${this.marginLeft}px`)
    }
    if (this.marginRight !== null) {
      this.el.style.setProperty('--divider-margin-right', `${this.marginRight}px`)
    }
  }

  /**
   * Ciclo de vida do componente.
   * Chamado quando o componente é conectado ao DOM.
   */
  connectedCallback() {
    this.hasDefaultSlotContent()
  }

  /**
   * Ciclo de vida do componente.
   * Chamado após o componente ser renderizado no DOM.
   */
  componentDidLoad() {
    this.setBleed()
    this.updateColor()
    this.updateMargins()
  }

  /**
   * Ciclo de vida do componente.
   * Chamado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    this.unsetBleed()
  }

  /**
   * Configura o divisor para sangrar (bleed) nas laterais.
   */
  private setBleed() {
    if (this.bleed) {
      this.applyMarginOnRootElement()
      window.addEventListener('resize', this.applyMarginOnRootElement.bind(this))
    }
  }

  /**
   * Remove o sangramento (bleed) do divisor.
   */
  private unsetBleed() {
    if (this.bleed) {
      window.removeEventListener('resize', this.applyMarginOnRootElement.bind(this))
    }
  }

  /**
   * Verifica se o divisor tem conteúdo no slot padrão.
   * @returns true se o divisor tiver conteúdo no slot padrão, false caso contrário.
   */
  private hasDefaultSlotContent(): boolean {
    return this.el.innerHTML.trim() !== ''
  }

  /**
   * Determina o paddingLeft e paddingRight do elemento pai do host.
   * @returns Um objeto contendo os valores de paddingLeft e paddingRight do elemento pai do host.
   */
  private getHostParentPadding() {
    const hostParent = this.el.parentElement
    if (hostParent !== null) {
      const style = getComputedStyle(hostParent)
      return { paddingLeft: parseFloat(style.paddingLeft), paddingRight: parseFloat(style.paddingRight) }
    }
    return { paddingLeft: 0, paddingRight: 0 }
  }

  /**
   * Aplica margens negativas no elemento raiz do divisor para compensar o padding do elemento pai.
   * Isso é necessário quando o divisor está configurado para sangrar (bleed).
   * IMPORTANTE: Preserva as variáveis CSS customizadas de margin.
   */
  private applyMarginOnRootElement() {
    const { paddingLeft, paddingRight } = this.getHostParentPadding()
    const rootElement = this.el.shadowRoot?.querySelector('.br-divider') as HTMLElement | null

    if (rootElement !== null) {
      rootElement.style.setProperty('margin-left', `-${paddingLeft}px`)
      rootElement.style.setProperty('margin-right', `-${paddingRight}px`)
    }
  }

  /**
   * Define as classes CSS que serão aplicadas ao divisor.
   * @returns Uma string contendo as classes CSS que serão aplicadas ao divisor.
   */
  private getCssClassMap() {
    return `br-divider divider-${this.align} ${this.orientation} ${this.borderStyle} ${this.thickness} ${this.isDarkMode ? 'dark-mode' : ''}`
  }

  /**
   * Renderiza o divisor quando há conteúdo no slot padrão.
   * @returns JSX.Element
   */
  private renderDefaultSlotDivider() {
    return (
      <div
        class={this.getCssClassMap()}
        role="separator"
        aria-orientation={this.orientation}
        aria-labelledby={this.customId}
      >
        <span id={this.customId}>
          <slot></slot>
        </span>
      </div>
    )
  }

  /**
   * Renderiza o divisor quando não há conteúdo no slot padrão.
   * @returns JSX.Element
   */
  private renderStandardDivider() {
    return <hr class={this.getCssClassMap()} />
  }

  render() {
    return <Host>{this.hasDefaultSlotContent() ? this.renderDefaultSlotDivider() : this.renderStandardDivider()}</Host>
  }
}
